import DaySwitch from '../../DaySwitch';
import Divider from '../../Divider';
import NumField from '../../NumField';
import Radio from '../../Radio';
import SimpleTable from '../../SimpeTable';
import { addDays } from 'date-fns/fp';
import { CapsuleTabContentBody, CapsuleTabContentHeader, ContentRow } from '../CapsuleComponents';
import DropDown from '../../DropDown';
import Button from '../../Button';
import Icon from '../../Icon';
import AddRemoveButton from '../../AddRemoveButton';
import { AvailabilityType } from '../../../semshared/availability/availability';
import { IFormState, defaultRoomOccupancy, parseDateOnly } from '../../../semshared/utils/lister';
import { TextTimeField } from '../../TextField';
import TimeField from '../../TimePicker';
import { LinkButton } from '../../LinkButton';
import { useTranslation } from 'react-i18next';

interface MeetingRoomsSectionProps {
  formState: IFormState
  requestFormStateUpdate: (newState: IFormState) => void;
  meetingRoomsDay: any;
  setMeetingRoomsDay: any;
  shadowRoot: any;
  availabilityChecker: AvailabilityType | null;
  setSimpleMode: (simpleMode: boolean) => void;
  simpleMode: boolean;
  setDrawerShow: (s: string) => void;
  setPopupShow: (s: string | null) => void;
}

/**
 * Popup editor for meeting room count and per-room settings.
 * @param props - Form state, day selection, and room update callbacks.
 * @returns Meeting-room section optimized for compact popup usage.
 */
const MeetingRoomsSectionPopup = ({ formState, requestFormStateUpdate, meetingRoomsDay, setMeetingRoomsDay, shadowRoot, availabilityChecker, simpleMode, setSimpleMode, setDrawerShow, setPopupShow }: MeetingRoomsSectionProps) => {
  const { t } = useTranslation();

  console.log("MeetingRoomsSectionPopup RENDERED. formState:", formState);

  const simple = () => {
    return <>
      <CapsuleTabContentHeader>
        {t('widget:headers.pleaseEnterRoomDetails')}
      </CapsuleTabContentHeader>
      <CapsuleTabContentBody>
      <div
          style={{
            display: 'flex',
            fontFamily: 'Roboto, sans-serif',
            justifyContent: 'space-between',
          }}
        >
        <NumField value={formState.days[0].occupancy.length} onChange={(v) => { // v is the new total number of rooms for all days
          const isAnyDayManuallyEdited = formState.days.some(d => d.seatCountsManuallyEdited === true);

          requestFormStateUpdate({
            ...formState,
            days: formState.days.map((d) => {
              const currentRoomCountForThisDay = d.occupancy.length;
              let updatedOccupancy = [...d.occupancy];
              let updatedSeating = [...d.seating];
              let updatedSeatCount = [...d.seatCount];

              if (v < currentRoomCountForThisDay) {
                // Reduce rooms
                updatedOccupancy = d.occupancy.slice(0, v);
                updatedSeating = d.seating.slice(0, v);
                updatedSeatCount = d.seatCount.slice(0, v);
              } else if (v > currentRoomCountForThisDay) {
                // Add rooms
                const diff = v - currentRoomCountForThisDay;
                for (let k = 0; k < diff; k++) {
                  updatedOccupancy.push(defaultRoomOccupancy());
                  updatedSeating.push('UFORM');
                }
                // Ensure seatCount array is long enough for new rooms.
                // If not in manual mode, this will be overridden by distribution.
                // If in manual mode, new rooms get a default.
                const defaultNewRoomSeatCount = v > 0 && d.totalGuests > 0 ? Math.ceil(d.totalGuests / v) : (d.totalGuests > 0 ? d.totalGuests : 0);
                const existingSeatCounts = [...d.seatCount];
                while (existingSeatCounts.length < currentRoomCountForThisDay) existingSeatCounts.push(0); // Pad if original was too short
                updatedSeatCount = [
                    ...existingSeatCounts.slice(0, currentRoomCountForThisDay),
                    ...Array(diff).fill(defaultNewRoomSeatCount)
                ];
              }

              // Ensure occupancy and seating arrays match the new room count 'v'
              if (updatedOccupancy.length !== v) {
                  if (v < updatedOccupancy.length) {
                      updatedOccupancy = updatedOccupancy.slice(0,v);
                  } else {
                      while(updatedOccupancy.length < v) updatedOccupancy.push(defaultRoomOccupancy());
                  }
              }
              if (updatedSeating.length !== v) {
                  if (v < updatedSeating.length) {
                      updatedSeating = updatedSeating.slice(0,v);
                  } else {
                      while(updatedSeating.length < v) updatedSeating.push('UFORM');
                  }
              }
              
              // Ensure seatCount array length matches v, especially if reducing rooms or if initial updatedSeatCount was insufficient
              if (updatedSeatCount.length > v) {
                updatedSeatCount = updatedSeatCount.slice(0, v);
              } else {
                const defaultForNewSeatCountSpots = v > 0 && d.totalGuests > 0 ? Math.ceil(d.totalGuests / v) : (d.totalGuests > 0 ? d.totalGuests : 0);
                while(updatedSeatCount.length < v) {
                    updatedSeatCount.push(defaultForNewSeatCountSpots);
                }
              }

              if (!isAnyDayManuallyEdited) {
                // Once any day is manually tuned, we avoid global auto-redistribution to preserve user intent.
                if (v === 0) {
                  updatedSeatCount = [];
                  updatedOccupancy = []; // Also clear occupancy and seating
                  updatedSeating = [];
                } else {
                  updatedSeatCount = Array(v).fill(0); 
                  if (d.totalGuests > 0) {
                    updatedSeatCount[0] = d.totalGuests; 
                    if (v > 1) {
                      const guestsPerRemainingRoom = Math.ceil(d.totalGuests / v);
                      for (let i = 1; i < v; i++) {
                        updatedSeatCount[i] = guestsPerRemainingRoom;
                      }
                    }
                  }
                }
              }
              // If isAnyDayManuallyEdited is true, updatedSeatCount remains as built from adding/removing rooms
              // without the general redistribution.

              // Final safety: if v is 0, ensure arrays are empty
              if (v === 0) {
                  updatedOccupancy = [];
                  updatedSeating = [];
                  updatedSeatCount = [];
              }
              
              return {
                ...d,
                occupancy: updatedOccupancy,
                seating: updatedSeating,
                seatCount: updatedSeatCount,
                // d.seatCountsManuallyEdited is NOT changed by this operation.
              };
            }),
          });
        }} />
        </div>
        <Divider />
        <LinkButton onClick={() => {
                            setDrawerShow('meeting_rooms')
                            setPopupShow(null)
                        }}>⇾ Details</LinkButton>
      </CapsuleTabContentBody>
    </>
  }

  const advanced = () => {
    return <>
      <CapsuleTabContentHeader>
        <ContentRow>
          {false && (
            <>
              <div
                style={{
                  display: 'inline-block',
                  marginRight: 10,
                  width: 120,
                  fontFamily: 'Roboto, sans-serif',
                }}
              >
                Seminarräume
              </div>
              <NumField
                value={formState.meetingRooms}
                onChange={value =>
                  requestFormStateUpdate({
                    ...formState,
                    meetingRooms: value,
                  })
                }
              />
              <Divider />
            </>
          )}
          <DaySwitch
            value={meetingRoomsDay}
            max={formState.days.length - 1}
            onChange={v => setMeetingRoomsDay(v)}
            date={addDays(meetingRoomsDay, parseDateOnly(formState.start))}
          />
        </ContentRow>
      </CapsuleTabContentHeader>
      <CapsuleTabContentBody>
        <SimpleTable
          verticalHeader={false}
          centeredHeader={true}
          header={['', t('widget:table.duration'), t('widget:table.seating'), t('widget:table.seatCount'), '']}
          data={formState.days[meetingRoomsDay].occupancy.map((m, index) => [
            <strong>{t('widget:table.room') + ' ' + (index + 1)}</strong>,
            <DropDown
              popupPosition="top"
              shadowRoot={shadowRoot}
              value={formState.days[meetingRoomsDay].occupancy[index].from}
              onChange={value =>
                requestFormStateUpdate({
                  ...formState,
                  days: formState.days.map((d, i) => {
                    if (i === meetingRoomsDay) {
                      return {
                        ...d,
                        occupancy: d.occupancy.map((s, j) => {
                          if (j === index) {
                            return value;
                          }
                          return s;
                        }),
                      };
                    }
                    return d;
                  }),
                })
              }
              options={[
                { value: 'FULLDAY', label: t('widget:time.fullDay') },
                { value: 'MORNING', label: t('widget:time.morning') },
                { value: 'AFTERNOON', label: t('widget:time.afternoon') },
              ]}
            />,
            <DropDown
              popupPosition="top"
              shadowRoot={shadowRoot}
              value={formState.days[meetingRoomsDay].seating[index]}
              onChange={value =>
                requestFormStateUpdate({
                  ...formState,
                  days: formState.days.map((d, i) => {
                    if (i === meetingRoomsDay) {
                      return {
                        ...d,
                        seating: d.seating.map((s, j) => {
                          if (j === index) {
                            return value;
                          }
                          return s;
                        }),
                      };
                    }
                    return d;
                  }),
                })
              }
              options={[
                {
                  value: 'UFORM',
                  label: 'U-Form',
                  icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/U-form.png`} />,
                },
                {
                  value: 'THEATER',
                  label: t('widget:seating.theater'),
                  icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/theater.png`} />,
                },
                {
                  value: 'PARLAMENT',
                  label: t('widget:seating.parliament'),
                  icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/parlament.png`} />,
                },
                {
                  value: 'CIRCLE',
                  label: t('widget:seating.circle'),
                  icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/kreis.png`} />,
                },
                {
                  value: 'BANKETT',
                  label: t('widget:seating.banquet'),
                  icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/bankett.png`} />,
                },
                {
                  value: 'COCKTAIL',
                  label: t('widget:seating.cocktail'),
                  icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/cocktail.png`} />,
                },
                {
                  value: 'BLOCK',
                  label: t('widget:seating.block'),
                  icon: <img src={`${process.env.REACT_APP_WIDGET_URL || ''}/assets/icons/block.png`} />,
                },
              ]}
            />,
            <NumField value={formState.days[meetingRoomsDay].seatCount[index]} onChange={value => requestFormStateUpdate({
              ...formState,
              days: formState.days.map((d, i) => {
                if (i === meetingRoomsDay) {
                  return {
                    ...d,
                    seatCount: d.seatCount.map((sc, j) => j === index ? value : sc)
                  };
                }
                return d;
              }),
            })} />,
            <AddRemoveButton
              iconOnly={true}
              onClick={() => {
                const newDays = formState.days[meetingRoomsDay];
                newDays.occupancy.splice(index, 1);
                newDays.seating.splice(index, 1);
                newDays.seatCount.splice(index, 1);
                requestFormStateUpdate({
                  ...formState,
                  days: formState.days.map((d, i) => {
                    if (i === meetingRoomsDay) {
                      return newDays;
                    }
                    return d;
                  }),
                });
              }}
            >
              -
            </AddRemoveButton>,
          ])}
        />
        <br />
        {<AddRemoveButton
          iconOnly={false}
          onClick={() => {
            const newDays = formState.days[meetingRoomsDay];
            newDays.occupancy.push(defaultRoomOccupancy());
            newDays.seating.push('UFORM');
            newDays.seatCount.push(10);
            requestFormStateUpdate({
              ...formState,
              days: formState.days.map((d, i) => {
                if (i === meetingRoomsDay) {
                  return newDays;
                }
                return d;
              }),
            });
          }}
        >
          + Seminarraum hinzufügen
        </AddRemoveButton>}
      </CapsuleTabContentBody>
    </>
  }

  return <>
    {simpleMode ? simple() : advanced()}
  </>
}

export default MeetingRoomsSectionPopup;
